import { filterContent } from './contentFilter';
import { supabase } from './supabase';
import type { UserPreferences } from '../components/ProfileModal';

interface UserContext {
  displayName?: string;
  age?: number;
  grade?: string;
  interests?: string[];
  interactionStyle?: string;
}

async function getUserContext(userId?: string): Promise<UserContext | null> {
  if (!userId) return null;

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('display_name, preferences')
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return {
      displayName: data.display_name,
      ...data.preferences
    };
  } catch (error) {
    console.error('Error fetching user context:', error);
    return null;
  }
}

function getAgeGroup(age?: number): 'child' | 'teen' | 'adult' {
  if (!age) return 'adult';
  if (age < 13) return 'child';
  if (age < 18) return 'teen';
  return 'adult';
}

function getGradeLevel(grade?: string): 'elementary' | 'middle' | 'high' | 'other' {
  if (!grade) return 'other';
  const gradeNum = parseInt(grade);
  if (isNaN(gradeNum)) {
    if (grade.toLowerCase().includes('kindergarten')) return 'elementary';
    return 'other';
  }
  if (gradeNum <= 5) return 'elementary';
  if (gradeNum <= 8) return 'middle';
  if (gradeNum <= 12) return 'high';
  return 'other';
}

function generatePersonaPrompt(context: UserContext | null): string {
  const basePersona = `You are Rumi, a friendly and supportive AI tutor focused on providing accurate, engaging, and educational content. Your responses should:

1. Prioritize Educational Value:
   - Focus on core academic subjects and practical skills
   - Provide clear, accurate explanations
   - Include relevant examples and real-world applications
   - Break down complex concepts into manageable steps

2. Maintain a Supportive Tone:
   - Be encouraging and positive
   - Celebrate effort and progress
   - Build confidence through constructive feedback
   - Use a warm, approachable teaching style

3. Foster Critical Thinking:
   - Encourage analytical and problem-solving skills
   - Ask guiding questions to promote understanding
   - Help students discover answers through guided exploration
   - Explain the reasoning behind solutions

4. Ensure Age-Appropriate Content:
   - Keep all responses suitable for all ages
   - Focus on academic and learning content
   - Avoid controversial or inappropriate topics
   - Use clear, respectful language

5. Promote Learning Best Practices:
   - Suggest study techniques and learning strategies
   - Encourage note-taking and practice
   - Recommend additional resources when relevant
   - Help develop good learning habits`;

  if (!context) return basePersona;

  const {
    displayName,
    age,
    grade,
    interests = [],
    interactionStyle
  } = context;

  const ageGroup = getAgeGroup(age);
  const gradeLevel = getGradeLevel(grade);

  let persona = basePersona;

  // Add name-based personalization
  if (displayName) {
    persona += `\n\nAddress the user as "${displayName}" in your responses when appropriate.`;
  }

  // Add age-based adjustments
  switch (ageGroup) {
    case 'child':
      persona += '\n\nFor young learners:\n- Use simple, clear language\n- Include fun, relatable examples\n- Offer frequent encouragement\n- Break concepts into very small steps';
      break;
    case 'teen':
      persona += '\n\nFor teenage learners:\n- Balance academic rigor with engagement\n- Connect concepts to real-world situations\n- Acknowledge and respect growing independence\n- Include study tips and learning strategies';
      break;
    case 'adult':
      persona += '\n\nFor adult learners:\n- Focus on practical applications\n- Respect prior knowledge and experience\n- Provide detailed explanations when requested\n- Suggest advanced resources when appropriate';
      break;
  }

  // Add grade-level adjustments
  switch (gradeLevel) {
    case 'elementary':
      persona += '\n\nFor elementary level:\n- Use visual and hands-on examples\n- Make learning fun and interactive\n- Celebrate small victories\n- Build foundational understanding';
      break;
    case 'middle':
      persona += '\n\nFor middle school level:\n- Connect different subjects and concepts\n- Encourage independent thinking\n- Address common misconceptions\n- Support developing study skills';
      break;
    case 'high':
      persona += '\n\nFor high school level:\n- Prepare for advanced concepts\n- Include college-prep context\n- Develop analytical skills\n- Support academic goals';
      break;
  }

  // Add interest-based personalization
  if (interests.length > 0) {
    persona += `\n\nIncorporate examples and analogies related to these interests when relevant: ${interests.join(', ')}.`;
  }

  // Add style-based adjustments
  switch (interactionStyle) {
    case 'casual':
      persona += '\n\nMaintain a relaxed, friendly tone while ensuring educational value.';
      break;
    case 'formal':
      persona += '\n\nUse proper academic language and structured explanations.';
      break;
    case 'fun':
      persona += '\n\nIncorporate appropriate humor and engaging elements while maintaining educational focus.';
      break;
    case 'professional':
      persona += '\n\nProvide clear, efficient explanations with a focus on practical application.';
      break;
  }

  return persona;
}

function extractMultimediaUrls(text: string): { text: string; media: { type: 'image' | 'video', url: string }[] } {
  const mediaUrls: { type: 'image' | 'video', url: string }[] = [];
  const mediaRegex = /\[(?:(image|video))\]\((https?:\/\/[^\s)]+)\)/g;
  
  const cleanText = text.replace(mediaRegex, (match, type, url) => {
    mediaUrls.push({ type: type as 'image' | 'video', url });
    return '';
  });

  return {
    text: cleanText.trim(),
    media: mediaUrls
  };
}

export async function getGeminiResponse(
  prompt: string,
  conversationHistory: string[] = [],
  userId?: string
): Promise<{ text: string; media: { type: 'image' | 'video', url: string }[] }> {
  try {
    // Content safety check
    const filterResult = await filterContent(prompt);
    if (filterResult.isBlocked) {
      throw new Error('I apologize, but I cannot assist with that type of content. Please keep questions focused on educational topics.');
    }

    if (!prompt?.trim()) {
      throw new Error('Please enter a question before submitting.');
    }

    const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    if (!apiKey) {
      throw new Error('API key not configured.');
    }

    // Get user context for personalization
    const userContext = await getUserContext(userId);
    const persona = generatePersonaPrompt(userContext);

    const context = conversationHistory.length > 0
      ? `Previous context:\n${conversationHistory.join('\n')}\n\nNew question:`
      : '';

    const fullPrompt = `${persona}

${context}
Student's question: ${prompt}

Please provide a helpful, encouraging response that:
1. Acknowledges the question
2. Explains the concept clearly
3. Provides step-by-step guidance if needed
4. Includes relevant examples
5. Ends with encouragement

Remember to maintain the specified tone and style, and incorporate the user's interests when relevant.`;

    const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: fullPrompt
          }]
        }]
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`API error: ${response.status} - ${JSON.stringify(errorData)}`);
    }

    const data = await response.json();

    if (!data.candidates || !data.candidates[0] || !data.candidates[0].content || !data.candidates[0].content.parts) {
      throw new Error('I apologize, but I was unable to generate a response. Please try asking your question in a different way.');
    }

    const responseText = data.candidates[0].content.parts[0].text;

    if (!responseText?.trim()) {
      throw new Error('I apologize, but I received an empty response. Please try asking your question again.');
    }

    const { text, media } = extractMultimediaUrls(responseText);

    return { text, media };
  } catch (error) {
    console.error('Error getting Gemini response:', error);
    throw error instanceof Error ? error : new Error('An unexpected error occurred. Please try again later.');
  }
}